import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import { organizationsApi } from './useOrganizationsApi';
import type {
  OrganizationDto,
  OrgMembershipDto,
  OrgMemberDto,
  SwitchOrgResponse,
  SetDefaultOrgResponse,
  MembershipRole,
} from './useOrganizationsApi';

/**
 * TanStack Query hook for fetching the current user's organization memberships.
 * Returns org details, role, and default indicator for each membership.
 * @param enabled - Whether the query should automatically run (default: true)
 */
export function useMyMembershipsQuery(enabled = true) {
  return useQuery<OrgMembershipDto[]>({
    queryKey: queryKeys.organizations.myMemberships(),
    queryFn: organizationsApi.getMyMemberships,
    enabled,
  });
}

/**
 * TanStack Query hook for fetching a single organization by ID.
 * @param orgId - Organization UUID
 * @param enabled - Whether the query should automatically run (default: true)
 */
export function useOrganizationDetailQuery(orgId: string, enabled = true) {
  return useQuery<OrganizationDto>({
    queryKey: queryKeys.organizations.detail(orgId),
    queryFn: () => organizationsApi.findById(orgId),
    enabled: enabled && !!orgId,
  });
}

/**
 * TanStack Query hook for fetching members of an organization.
 * @param orgId - Organization UUID
 * @param enabled - Whether the query should automatically run (default: true)
 */
export function useOrgMembersQuery(orgId: string, enabled = true) {
  return useQuery<OrgMemberDto[]>({
    queryKey: queryKeys.organizations.members(orgId),
    queryFn: () => organizationsApi.getMembers(orgId),
    enabled: enabled && !!orgId,
  });
}

/**
 * TanStack Query mutation hook for creating a new organization.
 * Invalidates memberships and org list on success.
 */
export function useCreateOrganizationMutation() {
  const queryClientInstance = useQueryClient();
  return useMutation<OrganizationDto, Error, { name: string }>({
    mutationFn: (data) => organizationsApi.create(data),
    onSuccess: () => {
      queryClientInstance.invalidateQueries({ queryKey: queryKeys.organizations.all });
    },
  });
}

/**
 * TanStack Query mutation hook for updating an organization.
 * Invalidates the specific org detail and memberships on success.
 */
export function useUpdateOrganizationMutation() {
  const queryClientInstance = useQueryClient();
  return useMutation<OrganizationDto, Error, { orgId: string; data: { name?: string } }>({
    mutationFn: ({ orgId, data }) => organizationsApi.update(orgId, data),
    onSuccess: (_data, variables) => {
      queryClientInstance.invalidateQueries({
        queryKey: queryKeys.organizations.detail(variables.orgId),
      });
      queryClientInstance.invalidateQueries({
        queryKey: queryKeys.organizations.myMemberships(),
      });
    },
  });
}

/**
 * TanStack Query mutation hook for deleting an organization.
 * Invalidates all org queries on success.
 */
export function useDeleteOrganizationMutation() {
  const queryClientInstance = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (orgId) => organizationsApi.remove(orgId),
    onSuccess: () => {
      queryClientInstance.invalidateQueries({ queryKey: queryKeys.organizations.all });
    },
  });
}

/**
 * TanStack Query mutation hook for switching the active organization.
 * Returns the new JWT token — caller is responsible for updating auth store.
 *
 * **Prefer `useAuthStore().switchOrg(orgId)` for interactive org switching.**
 * The store action handles the full side-effect chain: token update,
 * localStorage persistence, Zustand state, and WebSocket reconnection.
 * This mutation is provided for advanced use cases (e.g., background refresh).
 */
export function useSwitchOrgMutation() {
  const queryClientInstance = useQueryClient();
  return useMutation<SwitchOrgResponse, Error, string>({
    mutationFn: (orgId) => organizationsApi.switchOrg(orgId),
    onSuccess: () => {
      // Invalidate all queries since org context changed
      queryClientInstance.invalidateQueries();
    },
  });
}

/**
 * TanStack Query mutation hook for setting the default organization.
 * Invalidates memberships on success to reflect the new default.
 */
export function useSetDefaultOrgMutation() {
  const queryClientInstance = useQueryClient();
  return useMutation<SetDefaultOrgResponse, Error, string>({
    mutationFn: (orgId) => organizationsApi.setDefaultOrg(orgId),
    onSuccess: () => {
      queryClientInstance.invalidateQueries({
        queryKey: queryKeys.organizations.myMemberships(),
      });
    },
  });
}

/**
 * TanStack Query mutation hook for adding a member to an organization.
 * Invalidates the members list on success.
 */
export function useAddMemberMutation() {
  const queryClientInstance = useQueryClient();
  return useMutation<
    OrgMemberDto,
    Error,
    { orgId: string; data: { userId: string; role?: MembershipRole } }
  >({
    mutationFn: ({ orgId, data }) => organizationsApi.addMember(orgId, data),
    onSuccess: (_data, variables) => {
      queryClientInstance.invalidateQueries({
        queryKey: queryKeys.organizations.members(variables.orgId),
      });
    },
  });
}

/**
 * TanStack Query mutation hook for updating a member's role.
 * Invalidates the members list on success.
 */
export function useUpdateMemberRoleMutation() {
  const queryClientInstance = useQueryClient();
  return useMutation<
    OrgMemberDto,
    Error,
    { orgId: string; userId: string; data: { role: MembershipRole } }
  >({
    mutationFn: ({ orgId, userId, data }) => organizationsApi.updateMemberRole(orgId, userId, data),
    onSuccess: (_data, variables) => {
      queryClientInstance.invalidateQueries({
        queryKey: queryKeys.organizations.members(variables.orgId),
      });
    },
  });
}

/**
 * TanStack Query mutation hook for removing a member from an organization.
 * Invalidates the members list on success.
 */
export function useRemoveMemberMutation() {
  const queryClientInstance = useQueryClient();
  return useMutation<{ success: boolean }, Error, { orgId: string; userId: string }>({
    mutationFn: ({ orgId, userId }) => organizationsApi.removeMember(orgId, userId),
    onSuccess: (_data, variables) => {
      queryClientInstance.invalidateQueries({
        queryKey: queryKeys.organizations.members(variables.orgId),
      });
    },
  });
}
